import { type DragEvent, type ReactElement, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import type {
  BoardContents,
  BoardTimeline,
  TimelineCardEntry,
  TimelineGroupBy,
  TimelineLane,
} from '@molt/shared';
import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import {
  calendarDateFromKey,
  calendarDateKey,
  timestampDateKey,
} from '@/lib/board-calendar';
import {
  boardTimelineResizePatch,
  buildBoardTimelineScale,
  labelBoardTimelineColumns,
  placeBoardTimelineEntries,
  type BoardTimelineColumn,
  type BoardTimelineLabelledColumn,
  type BoardTimelinePlacedEntry,
  type BoardTimelineZoom,
} from '@/lib/board-timeline';
import { cardDisplayTitle } from '@/lib/card-title';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { boardViewAccounting, boardViewLabel } from '@/lib/view-state';

export interface BoardTimelineOpenCardIntent {
  readonly entry: TimelineCardEntry;
  readonly cardId: string;
}

export interface BoardTimelineMoveIntent {
  readonly entry: TimelineCardEntry;
  readonly cardId: string;
  readonly fromDateKey: string;
  readonly toDateKey: string;
  readonly laneId: string;
  readonly laneKind: TimelineLane['kind'];
}

export interface BoardTimelineResizeIntent {
  readonly entry: TimelineCardEntry;
  readonly cardId: string;
  readonly edge: 'start' | 'due';
  readonly fromDateKey: string;
  readonly toDateKey: string;
}

export interface BoardTimelineLaneIntent {
  readonly entry: TimelineCardEntry;
  readonly cardId: string;
  readonly fromLane: TimelineLane;
  readonly lane: TimelineLane;
}

export interface BoardTimelineScheduleIntent {
  readonly cardId: string;
  readonly fromListId: string;
  readonly lane: TimelineLane;
  readonly dateKey: string;
}

export interface BoardTimelineUnscheduledCard {
  readonly id: string;
  /**
   * ⚠ The card's RAW title, blank included, and the contract narrowed here
   * deliberately. This row used to carry `card.title.trim() || 'Untitled card'`
   * — a display fallback baked into a data shape by a module-level function
   * that cannot call `t` (`boardTimelineUnscheduledCardsFromContents` below is
   * EXPORTED and its production caller is `BoardFace.tsx`, a file this slice
   * may not edit). The helper now SELECTS cards rather than NAMING them; the
   * fallback is applied at each render site with `cardDisplayTitle(t, …)`.
   */
  readonly title: string;
  readonly path: string;
  readonly listId: string;
  readonly listTitle: string;
}

export interface BoardTimelineViewProps {
  readonly timeline: BoardTimeline;
  readonly zoom?: BoardTimelineZoom | undefined;
  readonly anchorDate?: Date | string | undefined;
  readonly readonly?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly unscheduledCards?: readonly BoardTimelineUnscheduledCard[] | undefined;
  readonly onZoomChange?: ((zoom: BoardTimelineZoom) => void) | undefined;
  readonly onAnchorDateChange?: ((date: Date) => void) | undefined;
  readonly onGroupByChange?: ((groupBy: TimelineGroupBy) => void) | undefined;
  readonly onOpenCard?: ((intent: BoardTimelineOpenCardIntent) => void) | undefined;
  readonly onMoveBar?: ((intent: BoardTimelineMoveIntent) => void) | undefined;
  readonly onResizeBar?: ((intent: BoardTimelineResizeIntent) => void) | undefined;
  readonly onMoveToLane?: ((intent: BoardTimelineLaneIntent) => void) | undefined;
  readonly onScheduleCard?: ((intent: BoardTimelineScheduleIntent) => void) | undefined;
}

type DragPayload =
  | {
      readonly mode: 'move';
      readonly cardId: string;
      readonly laneId: string;
    }
  | {
      readonly mode: 'resize';
      readonly cardId: string;
      readonly laneId: string;
      readonly edge: 'start' | 'due';
    };

const DRAG_MIME = 'application/x-molt-board-timeline-card';
const UNSCHEDULED_DRAG_MIME = 'application/x-molt-board-timeline-unscheduled-card';
const ZOOMS: readonly BoardTimelineZoom[] = ['day', 'week', 'month', 'quarter', 'year'];
const GROUPS: readonly TimelineGroupBy[] = ['none', 'list', 'member', 'label'];
const DRAWER_CARD_LIMIT = 100;

/**
 * ⚠ KEYS, not translated sentences. `statusKey` is React state that outlives
 * the render which set it, so a resolved sentence stored in it would still be
 * on screen in the previous language after the catalogue query resolves
 * underneath (`BoardMapView.tsx:80–84`; `BoardTableView.tsx`'s `dragRefusalKey`
 * is the worked example). `t` is applied at render, never at `setState`.
 *
 * ⚠ And `string | null`, never `''`. The old state cleared itself with
 * `setStatus('')`, and a key-or-empty-string union is exactly the type that
 * invites `setStatusKey(k === null ? null : t(k))` — a mutation that stores a
 * resolved sentence and survives an anchored `/setStatusKey\(\s*t\(/` regex.
 * The pin reads this setter's arguments with balanced parentheses instead.
 */
const STATUS_READONLY_DATES_KEY = 'boardTimeline.status.readonlyDates';
const STATUS_READONLY_DATES_LANES_KEY = 'boardTimeline.status.readonlyDatesLanes';
const STATUS_START_AFTER_DUE_KEY = 'boardTimeline.status.startAfterDue';
const STATUS_DUE_BEFORE_START_KEY = 'boardTimeline.status.dueBeforeStart';

export function boardTimelineUnscheduledCardsFromContents(
  contents: BoardContents,
): readonly BoardTimelineUnscheduledCard[] {
  const accountingIds = new Set(
    boardViewAccounting('timeline', contents).unrenderableCards.map((card) => card.id),
  );
  const rows: BoardTimelineUnscheduledCard[] = [];

  for (const list of contents.lists) {
    for (const card of list.cards) {
      if (!accountingIds.has(card.id)) continue;
      rows.push({
        id: card.id,
        title: card.title,
        path: card.path,
        listId: list.id,
        listTitle: list.title,
      });
    }
  }

  return rows;
}

export function BoardTimelineView({
  timeline,
  zoom: controlledZoom,
  anchorDate,
  readonly = false,
  busy = false,
  unscheduledCards = [],
  onZoomChange,
  onAnchorDateChange,
  onGroupByChange,
  onOpenCard,
  onMoveBar,
  onResizeBar,
  onMoveToLane,
  onScheduleCard,
}: BoardTimelineViewProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [localZoom, setLocalZoom] = useState<BoardTimelineZoom>(
    controlledZoom ?? 'month',
  );
  const [localAnchor, setLocalAnchor] = useState<Date | string>(
    anchorDate ?? timestampDateKey(timeline.range.from) ?? calendarDateKey(new Date()),
  );
  const [dragPayload, setDragPayload] = useState<DragPayload | null>(null);
  const [draggedUnscheduledId, setDraggedUnscheduledId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [statusKey, setStatusKey] = useState<string | null>(null);
  const zoom = controlledZoom ?? localZoom;
  const anchor = anchorDate ?? localAnchor;
  const scale = useMemo(
    () => buildBoardTimelineScale({ zoom, anchorDate: anchor }),
    [anchor, zoom],
  );
  /* ⚠ THE ONLY PLACE COLUMN NAMES ARE RESOLVED. `buildBoardTimelineScale` is
     pure and stays pure — `BoardFace.tsx` calls it for `.from`/`.to` alone —
     so the two names it used to compose in English arrive here as catalogue
     keys and become strings exactly once, on the way into the grid. */
  const columns = useMemo(() => labelBoardTimelineColumns(t, scale), [scale, t]);
  const placements = useMemo(
    () => placeBoardTimelineEntries(timeline.lanes, scale.columns),
    [scale.columns, timeline.lanes],
  );
  const visibleUnscheduledCards = unscheduledCards.slice(0, DRAWER_CARD_LIMIT);
  const hiddenUnscheduledCount = Math.max(
    unscheduledCards.length - visibleUnscheduledCards.length,
    0,
  );
  const unscheduledDisplayCount =
    unscheduledCards.length === 0
      ? timeline.unscheduledCardCount
      : unscheduledCards.length;

  function changeZoom(nextZoom: BoardTimelineZoom) {
    if (controlledZoom === undefined) setLocalZoom(nextZoom);
    onZoomChange?.(nextZoom);
  }

  function changeAnchor(nextDate: Date) {
    if (anchorDate === undefined) setLocalAnchor(nextDate);
    onAnchorDateChange?.(nextDate);
  }

  function handleDrop(
    event: DragEvent<HTMLElement>,
    lane: TimelineLane,
    column: BoardTimelineColumn,
  ) {
    event.preventDefault();
    const unscheduledId =
      event.dataTransfer.getData(UNSCHEDULED_DRAG_MIME) || draggedUnscheduledId;
    const unscheduledCard = unscheduledCards.find(
      (candidate) => candidate.id === unscheduledId,
    );
    if (unscheduledCard !== undefined) {
      if (readonly) {
        setStatusKey(STATUS_READONLY_DATES_KEY);
        return;
      }
      onScheduleCard?.({
        cardId: unscheduledCard.id,
        fromListId: unscheduledCard.listId,
        lane,
        dateKey: column.startKey,
      });
      return;
    }

    const payload = readDragPayload(event) ?? dragPayload;
    if (payload === null) return;
    const placement = placements.find(
      (candidate) =>
        candidate.entry.card.id === payload.cardId && candidate.laneId === payload.laneId,
    );
    if (placement === undefined) return;
    if (readonly) {
      setStatusKey(STATUS_READONLY_DATES_LANES_KEY);
      return;
    }
    if (payload.mode === 'resize') {
      emitResize(placement, payload.edge, column.startKey);
      return;
    }
    const fromLane =
      timeline.lanes.find((candidate) => candidate.id === placement.laneId) ?? lane;
    emitMove(placement, fromLane, lane, column.startKey);
  }

  function emitMove(
    placement: BoardTimelinePlacedEntry,
    fromLane: TimelineLane,
    lane: TimelineLane,
    toDateKey: string,
  ) {
    const fromDateKey = timelineEntryStartKey(placement.entry) ?? placement.startKey;
    if (fromDateKey !== toDateKey) {
      onMoveBar?.({
        entry: placement.entry,
        cardId: placement.entry.card.id,
        fromDateKey,
        toDateKey,
        laneId: lane.id,
        laneKind: lane.kind,
      });
    }
    if (placement.laneId !== lane.id) {
      onMoveToLane?.({
        entry: placement.entry,
        cardId: placement.entry.card.id,
        fromLane,
        lane,
      });
    }
  }

  function emitResize(
    placement: BoardTimelinePlacedEntry,
    edge: 'start' | 'due',
    toDateKey: string,
  ) {
    const fromDateKey =
      edge === 'start'
        ? timelineEntryStartKey(placement.entry)
        : timelineEntryDueKey(placement.entry);
    if (fromDateKey === null || fromDateKey === toDateKey) return;
    if (boardTimelineResizePatch(placement.entry, edge, toDateKey) === null) {
      setStatusKey(
        edge === 'start' ? STATUS_START_AFTER_DUE_KEY : STATUS_DUE_BEFORE_START_KEY,
      );
      return;
    }
    onResizeBar?.({
      entry: placement.entry,
      cardId: placement.entry.card.id,
      edge,
      fromDateKey,
      toDateKey,
    });
  }

  const middleColumn = columns[Math.floor(columns.length / 2)]!;
  /* ⚠ ONE key resolved ONCE and rendered TWICE — the `<h2>` and the grid's
     `aria-label`. The identity is authored, not coincidental, and
     `BoardTimelineView.test.tsx:242` finds the grid by the heading directly
     above it. Two keys would let a locale name the grid differently from its
     own heading, with nothing red. `drawerLabel` below is hoisted for the same
     reason: the `<aside>`'s accessible name and the `<h3>` inside it. */
  const title = t('boardTimeline.heading', { board: timeline.board.title });
  const drawerLabel = t('boardTimeline.drawerLabel');

  return (
    <section
      id="board-view-content"
      /* Composed, never declared: `BoardFace.tsx:5054` and `:5078` already name
         this same region in the error and loading branches of one conditional.
         This file held that defect verbatim as `aria-label="Timeline view"`. */
      aria-label={t('boardFace.viewRegion', { view: boardViewLabel(t, 'timeline') })}
      aria-busy={busy === true ? true : undefined}
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-surface px-3 pb-3 text-ink"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
          <p className="text-card text-ink-muted">
            {t('boardTimeline.summary', {
              column: middleColumn.label,
              count: timeline.unscheduledCardCount,
            })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-card text-ink-muted">
            <span>{t('boardTimeline.groupByLabel')}</span>
            <select
              value={timeline.groupBy}
              onChange={(event) =>
                onGroupByChange?.(event.currentTarget.value as TimelineGroupBy)
              }
              className="h-8 rounded-card border border-border-strong bg-surface-raised px-2 text-card text-ink"
            >
              {GROUPS.map((group) => (
                <option key={group} value={group}>
                  {groupLabel(t, group)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-card text-ink-muted">
            <span>{t('boardTimeline.zoomLabel')}</span>
            <select
              value={zoom}
              onChange={(event) =>
                changeZoom(event.currentTarget.value as BoardTimelineZoom)
              }
              className="h-8 rounded-card border border-border-strong bg-surface-raised px-2 text-card text-ink"
            >
              {ZOOMS.map((option) => (
                <option key={option} value={option}>
                  {zoomLabel(t, option)}
                </option>
              ))}
            </select>
          </label>

          <Button type="button" size="sm" onClick={() => changeAnchor(new Date())}>
            {t('boardTimeline.today')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            aria-expanded={drawerOpen}
            aria-controls="timeline-unscheduled-drawer"
            onClick={() => setDrawerOpen((open) => !open)}
          >
            {t('boardTimeline.openDrawer')}
          </Button>
        </div>
      </div>

      {/* ⚠ Rendered UNCONDITIONALLY, empty included, exactly as before the
          routing. A live region has to exist before the text arrives, and
          making it conditional would also change how many `role="status"`
          nodes this view has in its default state. */}
      <div role="status" className="sr-only">
        {statusKey === null ? null : t(statusKey)}
      </div>

      <div
        role="status"
        className="rounded-card border border-border bg-surface-raised px-3 py-2 text-card text-ink-muted"
      >
        {t(
          'boardTimeline.unscheduledSummary',
          { count: unscheduledDisplayCount },
          unscheduledDisplayCount,
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
        {drawerOpen ? (
          <aside
            id="timeline-unscheduled-drawer"
            aria-label={drawerLabel}
            className="flex w-72 shrink-0 flex-col overflow-hidden rounded-card border border-border-strong bg-panel"
          >
            <div className="border-b border-border px-3 py-2">
              <h3 className="text-card font-semibold text-ink">{drawerLabel}</h3>
              <p className="text-xs text-ink-muted">
                {t(
                  'boardTimeline.drawerCount',
                  { count: unscheduledDisplayCount },
                  unscheduledDisplayCount,
                )}
              </p>
            </div>
            <div className="max-h-full min-h-0 flex-1 overflow-y-auto p-2">
              {visibleUnscheduledCards.length === 0 ? (
                <p className="p-2 text-card text-ink-muted">
                  {t('boardTimeline.drawerEmpty')}
                </p>
              ) : (
                <ul aria-label={t('boardTimeline.drawerList')} className="grid gap-2">
                  {visibleUnscheduledCards.map((card) => (
                    <li key={card.id}>
                      <button
                        type="button"
                        draggable={!readonly}
                        aria-label={t('boardTimeline.scheduleCard', {
                          title: cardDisplayTitle(t, card.title),
                          list: card.listTitle,
                        })}
                        disabled={readonly}
                        title={readonly ? t('boardTimeline.readonlyReason') : undefined}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = readonly ? 'none' : 'move';
                          event.dataTransfer.setData(UNSCHEDULED_DRAG_MIME, card.id);
                          setDraggedUnscheduledId(card.id);
                          setStatusKey(null);
                        }}
                        className="w-full rounded-card border border-border bg-surface-raised px-3 py-2 text-left text-card text-ink shadow-card hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="block truncate font-medium">
                          {cardDisplayTitle(t, card.title)}
                        </span>
                        <span className="mt-1 block truncate text-xs text-ink-muted">
                          {card.listTitle}
                        </span>
                      </button>
                    </li>
                  ))}
                  {hiddenUnscheduledCount > 0 ? (
                    <li className="rounded-card border border-dashed border-border px-3 py-2 text-card text-ink-muted">
                      {t(
                        'boardTimeline.drawerOverflow',
                        { count: hiddenUnscheduledCount },
                        hiddenUnscheduledCount,
                      )}
                    </li>
                  ) : null}
                </ul>
              )}
            </div>
          </aside>
        ) : null}

        <div
          role="grid"
          aria-label={title}
          className="min-h-0 flex-1 overflow-auto rounded-card border border-border-strong bg-panel"
        >
          <div
            role="row"
            className="sticky top-0 z-20 grid border-b border-border bg-surface-raised text-xs font-semibold text-ink-muted"
            style={{ gridTemplateColumns: timelineGridColumns(columns.length) }}
          >
            <div
              role="columnheader"
              className="sticky left-0 z-30 bg-surface-raised px-3 py-2"
            >
              {t('boardTimeline.laneColumn')}
            </div>
            {columns.map((column) => (
              <div
                key={column.key}
                role="columnheader"
                className={cn(
                  'border-l border-border px-2 py-2',
                  column.isToday ? 'text-accent' : null,
                )}
              >
                {column.shortLabel}
              </div>
            ))}
          </div>

          {timeline.lanes.map((lane) => (
            <TimelineLaneRow
              key={lane.id}
              lane={lane}
              columns={columns}
              placements={placements.filter((placement) => placement.laneId === lane.id)}
              readonly={readonly}
              onOpenCard={onOpenCard}
              onDragStart={(payload) => {
                setStatusKey(null);
                setDragPayload(payload);
              }}
              onDrop={handleDrop}
              onMove={emitMove}
              onResize={emitResize}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface TimelineLaneRowProps {
  readonly lane: TimelineLane;
  /* ⚠ LABELLED, not bare. This row draws `column.label` into
     `boardTimeline.cellLabel`; the bare `BoardTimelineColumn` no longer carries
     a name, which is what makes an un-resolved column a type error here. */
  readonly columns: readonly BoardTimelineLabelledColumn[];
  readonly placements: readonly BoardTimelinePlacedEntry[];
  readonly readonly: boolean;
  readonly onOpenCard?: ((intent: BoardTimelineOpenCardIntent) => void) | undefined;
  readonly onDragStart: (payload: DragPayload) => void;
  readonly onDrop: (
    event: DragEvent<HTMLElement>,
    lane: TimelineLane,
    column: BoardTimelineColumn,
  ) => void;
  readonly onMove: (
    placement: BoardTimelinePlacedEntry,
    fromLane: TimelineLane,
    lane: TimelineLane,
    toDateKey: string,
  ) => void;
  readonly onResize: (
    placement: BoardTimelinePlacedEntry,
    edge: 'start' | 'due',
    toDateKey: string,
  ) => void;
}

function TimelineLaneRow({
  lane,
  columns,
  placements,
  readonly,
  onOpenCard,
  onDragStart,
  onDrop,
  onMove,
  onResize,
}: TimelineLaneRowProps): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <div
      role="row"
      className="relative grid min-h-24 border-b border-border last:border-b-0"
      style={{ gridTemplateColumns: timelineGridColumns(columns.length) }}
    >
      <div
        role="rowheader"
        className="sticky left-0 z-10 flex items-center border-r border-border bg-panel px-3 py-3 text-card font-semibold text-ink"
      >
        {/* ⚠ NOT routable here, and not an oversight: `lane.title` is
            SERVER-authored English (`apps/server/src/routes/timeline-view.ts`
            writes `Cards`, `Unassigned`, `Unlabelled` into it). No served
            catalogue can translate a string that arrives in a response body.
            Reported as a contract bug; `apps/server` is out of bounds. */}
        <span className="truncate">{lane.title}</span>
      </div>

      {columns.map((column) => (
        <div
          key={column.key}
          role="gridcell"
          aria-label={t('boardTimeline.cellLabel', {
            lane: lane.title,
            column: column.label,
          })}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => onDrop(event, lane, column)}
          className={cn(
            'min-h-24 border-l border-border px-1 py-2',
            column.isToday ? 'bg-surface-raised' : null,
          )}
        />
      ))}

      <div
        className="pointer-events-none absolute inset-y-0 left-[10rem] right-0 grid items-center gap-y-1 px-1 py-2"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(4rem, 1fr))` }}
      >
        {placements.map((placement) => (
          <TimelineBar
            key={`${lane.id}:${placement.entry.card.id}`}
            placement={placement}
            lane={lane}
            columns={columns}
            readonly={readonly}
            onOpenCard={onOpenCard}
            onDragStart={onDragStart}
            onMove={onMove}
            onResize={onResize}
          />
        ))}
      </div>
    </div>
  );
}

interface TimelineBarProps {
  readonly placement: BoardTimelinePlacedEntry;
  readonly lane: TimelineLane;
  readonly columns: readonly BoardTimelineColumn[];
  readonly readonly: boolean;
  readonly onOpenCard?: ((intent: BoardTimelineOpenCardIntent) => void) | undefined;
  readonly onDragStart: (payload: DragPayload) => void;
  readonly onMove: (
    placement: BoardTimelinePlacedEntry,
    fromLane: TimelineLane,
    lane: TimelineLane,
    toDateKey: string,
  ) => void;
  readonly onResize: (
    placement: BoardTimelinePlacedEntry,
    edge: 'start' | 'due',
    toDateKey: string,
  ) => void;
}

function TimelineBar({
  placement,
  lane,
  columns,
  readonly,
  onOpenCard,
  onDragStart,
  onMove,
  onResize,
}: TimelineBarProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const entry = placement.entry;
  const title = cardDisplayTitle(t, entry.card.title);
  const range = timelineRangeLabel(t, entry);
  const currentIndex = columns.findIndex(
    (column) => column.startKey === placement.startKey,
  );
  const previousColumn = currentIndex > 0 ? (columns[currentIndex - 1] ?? null) : null;
  const nextColumn =
    currentIndex < columns.length - 1 ? (columns[currentIndex + 1] ?? null) : null;
  const dueIndex = columns.findIndex(
    (column) => column.endKey >= (timelineEntryDueKey(entry) ?? placement.endKey),
  );
  const previousDueColumn = dueIndex > 0 ? (columns[dueIndex - 1] ?? null) : null;
  const nextDueColumn =
    dueIndex >= 0 && dueIndex < columns.length - 1
      ? (columns[dueIndex + 1] ?? null)
      : null;
  const payload: DragPayload = {
    mode: 'move',
    cardId: entry.card.id,
    laneId: placement.laneId,
  };
  /* Resolved once and spliced into `{reason}`; every `{action}` stays an inline
     `t('…')`. See the calendar's twin comment for why a `controlLabel` helper
     is refused rather than merely unwritten. */
  const readonlyReason = t('boardTimeline.readonlyReason');

  return (
    <div
      className="pointer-events-auto min-w-0 rounded-card border border-border-strong bg-surface-raised p-1 shadow-card"
      style={{ gridColumn: `${placement.startColumnIndex + 1} / span ${placement.span}` }}
    >
      <div className="flex min-w-0 items-center gap-1">
        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveStart', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveStart', { title })
          }
          draggable
          disabled={readonly}
          title={readonly ? readonlyReason : undefined}
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = readonly ? 'none' : 'move';
            const resizePayload: DragPayload = {
              mode: 'resize',
              cardId: entry.card.id,
              laneId: placement.laneId,
              edge: 'start',
            };
            writeDragPayload(event, resizePayload);
            onDragStart(resizePayload);
          }}
        >
          <ChevronsLeft className="size-3.5" />
        </IconButton>

        <button
          type="button"
          draggable
          aria-label={t('boardTimeline.openCard', { title, range })}
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = readonly ? 'none' : 'move';
            writeDragPayload(event, payload);
            onDragStart(payload);
          }}
          onClick={() => onOpenCard?.({ entry, cardId: entry.card.id })}
          className={cn(
            'min-w-0 flex-1 truncate rounded-card px-2 py-1 text-left text-xs font-medium text-ink hover:bg-panel',
            entry.completed ? 'line-through opacity-70' : null,
          )}
        >
          {title}
          <span className="mt-0.5 block truncate text-[0.6875rem] font-normal text-ink-muted">
            {range}
          </span>
        </button>

        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveDue', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveDue', { title })
          }
          draggable
          disabled={readonly}
          title={readonly ? readonlyReason : undefined}
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = readonly ? 'none' : 'move';
            const resizePayload: DragPayload = {
              mode: 'resize',
              cardId: entry.card.id,
              laneId: placement.laneId,
              edge: 'due',
            };
            writeDragPayload(event, resizePayload);
            onDragStart(resizePayload);
          }}
        >
          <ChevronsRight className="size-3.5" />
        </IconButton>
      </div>

      <div className="mt-1 flex items-center justify-end gap-0.5">
        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveEarlier', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveEarlier', { title })
          }
          disabled={readonly || previousColumn === null}
          title={readonly ? readonlyReason : undefined}
          onClick={() => {
            if (previousColumn !== null)
              onMove(placement, lane, lane, previousColumn.startKey);
          }}
        >
          <ArrowLeft className="size-3.5" />
        </IconButton>
        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveLater', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveLater', { title })
          }
          disabled={readonly || nextColumn === null}
          title={readonly ? readonlyReason : undefined}
          onClick={() => {
            if (nextColumn !== null) onMove(placement, lane, lane, nextColumn.startKey);
          }}
        >
          <ArrowRight className="size-3.5" />
        </IconButton>
        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveStartEarlier', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveStartEarlier', { title })
          }
          disabled={readonly || previousColumn === null}
          title={readonly ? readonlyReason : undefined}
          onClick={() => {
            if (previousColumn !== null)
              onResize(placement, 'start', previousColumn.startKey);
          }}
        >
          <ChevronsLeft className="size-3.5" />
        </IconButton>
        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveStartLater', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveStartLater', { title })
          }
          disabled={readonly || nextColumn === null}
          title={readonly ? readonlyReason : undefined}
          onClick={() => {
            if (nextColumn !== null) onResize(placement, 'start', nextColumn.startKey);
          }}
        >
          <ChevronsRight className="size-3.5" />
        </IconButton>
        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveDueEarlier', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveDueEarlier', { title })
          }
          disabled={readonly || previousDueColumn === null}
          title={readonly ? readonlyReason : undefined}
          onClick={() => {
            if (previousDueColumn !== null)
              onResize(placement, 'due', previousDueColumn.startKey);
          }}
        >
          <ChevronsLeft className="size-3.5" />
        </IconButton>
        <IconButton
          size="sm"
          label={
            readonly
              ? t('boardTimeline.controlUnavailable', {
                  action: t('boardTimeline.moveDueLater', { title }),
                  reason: readonlyReason,
                })
              : t('boardTimeline.moveDueLater', { title })
          }
          disabled={readonly || nextDueColumn === null}
          title={readonly ? readonlyReason : undefined}
          onClick={() => {
            if (nextDueColumn !== null)
              onResize(placement, 'due', nextDueColumn.startKey);
          }}
        >
          <ChevronsRight className="size-3.5" />
        </IconButton>
      </div>
    </div>
  );
}

function writeDragPayload(event: DragEvent<HTMLElement>, payload: DragPayload): void {
  event.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
}

function readDragPayload(event: DragEvent<HTMLElement>): DragPayload | null {
  const raw = event.dataTransfer.getData(DRAG_MIME);
  if (raw === '') return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DragPayload>;
    if (
      parsed.mode === 'move' &&
      typeof parsed.cardId === 'string' &&
      typeof parsed.laneId === 'string'
    ) {
      return { mode: 'move', cardId: parsed.cardId, laneId: parsed.laneId };
    }
    if (
      parsed.mode === 'resize' &&
      typeof parsed.cardId === 'string' &&
      typeof parsed.laneId === 'string' &&
      (parsed.edge === 'start' || parsed.edge === 'due')
    ) {
      return {
        mode: 'resize',
        cardId: parsed.cardId,
        laneId: parsed.laneId,
        edge: parsed.edge,
      };
    }
  } catch {
    return null;
  }
  return null;
}

function timelineEntryStartKey(entry: TimelineCardEntry): string | null {
  if (entry.startAt !== null) return timestampDateKey(entry.startAt);
  return entry.dueAt === null ? null : timestampDateKey(entry.dueAt);
}

function timelineEntryDueKey(entry: TimelineCardEntry): string | null {
  if (entry.dueAt !== null) return timestampDateKey(entry.dueAt);
  return entry.startAt === null ? null : timestampDateKey(entry.startAt);
}

/** ⚠ `t` FIRST and REQUIRED — `lib/card-title.ts:19–23`'s idiom, applied to
 *  the three module-level helpers this file kept. */
function timelineRangeLabel(
  t: InterfaceLanguageContextValue['t'],
  entry: TimelineCardEntry,
): string {
  const startKey = entry.startAt === null ? null : timestampDateKey(entry.startAt);
  const dueKey = entry.dueAt === null ? null : timestampDateKey(entry.dueAt);
  if (startKey !== null && dueKey !== null && startKey !== dueKey) {
    return t('boardTimeline.rangeSpan', {
      start: longDateLabel(startKey),
      due: longDateLabel(dueKey),
    });
  }
  const pointKey = startKey ?? dueKey;
  return pointKey === null
    ? t('boardTimeline.rangeUnscheduled')
    : longDateLabel(pointKey);
}

function groupLabel(
  t: InterfaceLanguageContextValue['t'],
  group: TimelineGroupBy,
): string {
  if (group === 'none') return t('boardTimeline.groupBy.none');
  if (group === 'list') return t('boardTimeline.groupBy.list');
  if (group === 'member') return t('boardTimeline.groupBy.member');
  return t('boardTimeline.groupBy.label');
}

function zoomLabel(
  t: InterfaceLanguageContextValue['t'],
  zoom: BoardTimelineZoom,
): string {
  if (zoom === 'day') return t('boardTimeline.zoom.day');
  if (zoom === 'week') return t('boardTimeline.zoom.week');
  if (zoom === 'month') return t('boardTimeline.zoom.month');
  if (zoom === 'quarter') return t('boardTimeline.zoom.quarter');
  return t('boardTimeline.zoom.year');
}

/**
 * ⚠ NOT ROUTED, and REPORTED rather than fixed — the twin of
 * `BoardCalendarView.tsx`'s helper of the same name, and the same reasoning:
 * `undefined` is the browser's locale, and `lib/locale-formatting.ts` holds no
 * formatter with this option set, so adopting one changes rendered bytes that
 * `FR-VIEW-*` specs bind.
 */
function longDateLabel(dateKey: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(calendarDateFromKey(dateKey));
}

function timelineGridColumns(columnCount: number): string {
  return `10rem repeat(${columnCount}, minmax(4rem, 1fr))`;
}
